import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '@/api';
import type { ChatSendBody } from '@/api/endpoints';
import { queryKeys } from '@/api/query-keys';
import { Badge, Button, Drawer, Icon, Textarea } from '@/ui';
import { useUiPrefs } from '@/store/ui-prefs';
import type { ChatMessage, ChatSession } from '@/types/api';

/**
 * Atlas chat — the right-side assistant.
 *
 * Backs onto the existing /api/chat infrastructure: per-user session that
 * survives page changes, tool calls that auto-execute when read-only, and
 * mutating proposals that wait for an Apply/Cancel on a card here.
 *
 * Rendered as a Drawer overlay app-wide, EXCEPT on /projects/new — that
 * page embeds the chat inline beside the form (see NewProject.tsx), so we
 * suppress the drawer here to avoid two chat panels showing the same
 * conversation on top of each other.
 *
 * The per-turn page scope is computed from the current route, so phrases
 * like "this project" / "this initiative" stay attached to whatever the
 * user is looking at when they hit Send.
 */
export function RightChatPanel() {
  const { chatOpen, setChatOpen } = useUiPrefs();
  const location = useLocation();
  // /projects/new hosts the chat inline next to the form — don't double-render
  // the drawer or its scrim, which would block clicks on the form.
  if (location.pathname === '/projects/new') return null;
  return (
    <Drawer open={chatOpen} onClose={() => setChatOpen(false)} width={480} side="right">
      <ChatPanelInner onClose={() => setChatOpen(false)} />
    </Drawer>
  );
}

interface ChatPanelInnerProps {
  /** Show a close (×) button in the header. Omit for inline embeds. */
  onClose?: () => void;
  /**
   * Per-send extra fields merged into the chat.send body. Called fresh
   * for each send so changing state (e.g. live form values) is captured.
   * Use this on /projects/new to attach intake_form_state.
   */
  extraSend?: () => Partial<ChatSendBody>;
}

/**
 * The chat panel body — header, message list, composer. No drawer chrome
 * so it can be embedded inline next to other content (see NewProject).
 *
 * Reads session via the shared queryKeys.chat.session query, so every
 * mount (drawer or inline) sees the same conversation in real time.
 */
export function ChatPanelInner({ onClose, extraSend }: ChatPanelInnerProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const scope = useMemo(() => deriveScope(location.pathname), [location.pathname]);

  const sessionQ = useQuery<ChatSession>({
    queryKey: queryKeys.chat.session,
    queryFn: () => api.chat.session(),
    refetchOnWindowFocus: false,
  });

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (message: string) =>
      api.chat.send({
        message,
        page_scope_type: scope?.type,
        page_scope_id: scope?.id,
        ...(extraSend ? extraSend() : {}),
      }),
    onSuccess: (out) => {
      // Optimistically append the new messages onto the cached session.
      queryClient.setQueryData<ChatSession>(queryKeys.chat.session, (prev) =>
        prev ? { ...prev, messages: [...prev.messages, ...out.new_messages] } : prev,
      );
    },
  });

  const apply = useMutation({
    mutationFn: (id: number) => api.chat.apply(id),
    onSuccess: ({ message, followup }) => {
      queryClient.setQueryData<ChatSession>(queryKeys.chat.session, (prev) => {
        if (!prev) return prev;
        const messages = prev.messages.map((m) => (m.id === message.id ? message : m));
        return { ...prev, messages: [...messages, ...followup] };
      });
    },
  });

  const cancel = useMutation({
    mutationFn: (id: number) => api.chat.cancel(id),
    onSuccess: (updated) => {
      queryClient.setQueryData<ChatSession>(queryKeys.chat.session, (prev) =>
        prev
          ? { ...prev, messages: prev.messages.map((m) => (m.id === updated.id ? updated : m)) }
          : prev,
      );
    },
  });

  const clear = useMutation({
    mutationFn: () => api.chat.clear(),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKeys.chat.session, fresh);
    },
  });

  // Auto-scroll to bottom whenever messages change.
  const messageCount = sessionQ.data?.messages.length ?? 0;
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messageCount, send.isPending]);

  const isIntake = sessionQ.data?.mode === 'intake';

  const onSubmit = () => {
    const text = draft.trim();
    if (!text || send.isPending) return;
    setDraft('');
    send.mutate(text);
  };

  return (
    <>
      <header
        style={{
          padding: 'var(--apm-card-pad)',
          borderBottom: '1px solid var(--apm-line)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--apm-muted)',
              marginBottom: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name={isIntake ? 'ideas' : 'sparkle'} size={12} />
            {isIntake ? 'Atlas · intake interview' : 'Atlas chat'}
            {isIntake && (
              <Badge tone="accent" size="sm">Intake</Badge>
            )}
            {!isIntake && scope && (
              <Badge tone="info" size="sm">
                {scope.type}
                {scope.id != null ? ` #${scope.id}` : ''}
              </Badge>
            )}
          </div>
          <div
            style={{
              fontFamily: 'var(--apm-font-display)',
              fontSize: 'var(--apm-text-xl)',
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            {isIntake ? "Let's build something new" : 'Ask anything'}
          </div>
        </div>
        <Button
          variant="ghost"
          icon={<Icon name="refresh" size={12} />}
          onClick={() => clear.mutate()}
          title="Start a fresh conversation"
        />
        {onClose && (
          <Button
            variant="ghost"
            icon={<Icon name="x" size={14} />}
            onClick={onClose}
            title="Close"
          />
        )}
      </header>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--apm-card-pad)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: 'var(--apm-bg)',
        }}
      >
        {sessionQ.isLoading ? (
          <div style={{ color: 'var(--apm-muted)', fontSize: 13 }}>Loading…</div>
        ) : (sessionQ.data?.messages ?? []).length === 0 ? (
          isIntake ? <IntakeEmptyHint /> : <EmptyHint />
        ) : (
          sessionQ.data!.messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onApply={() => apply.mutate(m.id)}
              onCancel={() => cancel.mutate(m.id)}
              busy={apply.isPending || cancel.isPending}
            />
          ))
        )}
        {send.isPending && (
          <div
            style={{
              alignSelf: 'flex-start',
              color: 'var(--apm-muted)',
              fontSize: 12.5,
              fontStyle: 'italic',
            }}
          >
            Thinking…
          </div>
        )}
        {send.isError && (
          <div
            role="alert"
            style={{
              alignSelf: 'flex-start',
              padding: '8px 12px',
              background: 'var(--apm-risk-soft)',
              color: 'var(--apm-risk)',
              borderRadius: 'var(--apm-radius)',
              fontSize: 12.5,
            }}
          >
            Could not reach Atlas. Check the Claude API key in Help → Atlas
            status, then try again.
          </div>
        )}
      </div>

      <div
        style={{
          padding: 'var(--apm-card-pad)',
          borderTop: '1px solid var(--apm-line)',
        }}
      >
        <Textarea
          value={draft}
          onChange={setDraft}
          placeholder={
            isIntake
              ? 'Tell me what you want to build. I’ll ask questions and we’ll fill out the form together…'
              : scope
                ? `Ask about this ${scope.type}, or anything else…`
                : 'Ask about projects, ideas, components — anything in the portfolio.'
          }
          rows={3}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--apm-muted)' }}>
            Enter sends · Shift+Enter for newline
          </div>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={!draft.trim() || send.isPending}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </>
  );
}

function EmptyHint() {
  return (
    <div
      style={{
        padding: 'var(--apm-card-pad)',
        background: 'var(--apm-card-2)',
        border: '1px solid var(--apm-line)',
        borderRadius: 'var(--apm-radius-lg)',
        fontSize: 13.5,
        lineHeight: 1.55,
        color: 'var(--apm-ink-2)',
      }}
    >
      <div style={{ fontWeight: 500, color: 'var(--apm-ink)', marginBottom: 6 }}>
        Start a conversation
      </div>
      Try things like:
      <ul style={{ margin: '8px 0 0 18px', padding: 0, color: 'var(--apm-muted)' }}>
        <li>“Which projects involve PHI?”</li>
        <li>“Summarize the Avalara project.”</li>
        <li>“Suggest a project to spin up around accounts payable.”</li>
      </ul>
    </div>
  );
}

function IntakeEmptyHint() {
  return (
    <div
      style={{
        padding: 'var(--apm-card-pad)',
        background: 'var(--apm-accent-soft)',
        border: '1px solid color-mix(in oklab, var(--apm-accent) 30%, var(--apm-line))',
        borderRadius: 'var(--apm-radius-lg)',
        fontSize: 13.5,
        lineHeight: 1.55,
        color: 'var(--apm-ink-2)',
      }}
    >
      <div style={{ fontWeight: 500, color: 'var(--apm-accent-ink)', marginBottom: 6 }}>
        Let's build something new
      </div>
      Tell me the idea in your own words. I'll ask the right questions to shape
      it — scope, who it's for, what data it touches, whether there's anything
      similar already in the portfolio. <strong>You'll see the form on the
      left fill in as we talk</strong>; edit any field to take it back.
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: 'var(--apm-muted)',
          fontStyle: 'italic',
        }}
      >
        Try: “I want a tool that automates the AP aging report” or “We need a
        way to review purchase orders before signoff.”
      </div>
    </div>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  onApply: () => void;
  onCancel: () => void;
  busy: boolean;
}

function MessageBubble({ message, onApply, onCancel, busy }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isToolResult = message.role === 'tool_result';

  if (isToolResult) {
    // Tool results are typically quiet; the assistant message that follows
    // is what the user reads. We only show them as a tiny footer if there's
    // no body. Skip rendering by default.
    return null;
  }

  const call = (message.tool_call as
    | { name?: string; input?: Record<string, unknown> }
    | null
    | undefined) ?? null;
  const isFormPatch = isAssistant && call?.name === 'update_intake_form';
  const proposal = isAssistant && !!message.tool_call && message.tool_status === 'pending';
  const proposalSettled =
    isAssistant && !!message.tool_call && (message.tool_status === 'applied' || message.tool_status === 'cancelled');
  const tone = isUser ? 'user' : 'assistant';

  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '88%',
      }}
    >
      {message.text && (
        <div
          style={{
            padding: '10px 14px',
            background: tone === 'user' ? 'var(--apm-ink)' : 'var(--apm-card)',
            color: tone === 'user' ? 'var(--apm-bg-2)' : 'var(--apm-ink)',
            border: tone === 'user' ? 'none' : '1px solid var(--apm-line)',
            borderRadius: 'var(--apm-radius-lg)',
            fontSize: 13.5,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {message.text}
        </div>
      )}
      {isFormPatch && call?.input && (
        <FormPatchNote input={call.input} />
      )}
      {proposal && (
        <ProposalCard
          message={message}
          onApply={onApply}
          onCancel={onCancel}
          busy={busy}
        />
      )}
      {proposalSettled && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11.5,
            color: 'var(--apm-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name={message.tool_status === 'applied' ? 'check' : 'x'} size={11} />
          {message.tool_status === 'applied' ? 'Applied' : 'Cancelled'}
          {message.applied_by && message.tool_status === 'applied'
            ? ` by ${message.applied_by.name}`
            : ''}
        </div>
      )}
    </div>
  );
}

function ProposalCard({
  message,
  onApply,
  onCancel,
  busy,
}: {
  message: ChatMessage;
  onApply: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const call = (message.tool_call as
    | {
        name?: string;
        input?: Record<string, unknown>;
        preview?: string;
        mutating?: boolean;
      }
    | null) ?? null;
  if (!call) return null;
  return (
    <div
      style={{
        marginTop: 8,
        padding: 12,
        background: 'var(--apm-accent-soft)',
        border: '1px solid color-mix(in oklab, var(--apm-accent) 30%, var(--apm-line))',
        borderRadius: 'var(--apm-radius-lg)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--apm-accent-ink)',
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        Atlas wants to {call.name?.replace(/_/g, ' ')}
      </div>
      {call.preview && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--apm-ink)',
            lineHeight: 1.5,
            marginBottom: 10,
            whiteSpace: 'pre-wrap',
          }}
        >
          {call.preview}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="accent" size="sm" onClick={onApply} disabled={busy}>
          {busy ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}

// Renders the inline "✨ Filled: name, goal, …" footnote under an
// update_intake_form tool call. The actual form mutation happens on the
// NewProject page — this is just a transparent receipt for the user.
function FormPatchNote({ input }: { input: Record<string, unknown> }) {
  const fieldLabels: Record<string, string> = {
    name: 'name',
    goal: 'goal',
    business_case: 'business case',
    owner_id: 'owner',
    sponsor_id: 'sponsor',
    developer_id: 'developer',
    user_base_type: 'user base',
    user_base_size: 'user count',
    involves_phi_pii: 'PHI/PII flag',
    phi_pii_description: 'PHI/PII notes',
    target_go_live: 'go-live date',
    sharepoint_url: 'SharePoint URL',
  };
  const filled = Object.entries(input)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k]) => fieldLabels[k] ?? k);
  if (filled.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 6,
        padding: '4px 10px',
        background: 'var(--apm-accent-soft)',
        border: '1px dashed color-mix(in oklab, var(--apm-accent) 30%, var(--apm-line))',
        borderRadius: 'var(--apm-radius)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11.5,
        color: 'var(--apm-accent-ink)',
      }}
    >
      <Icon name="sparkle" size={10} />
      <span>Filled in: {filled.join(', ')}</span>
    </div>
  );
}

function deriveScope(pathname: string): { type: string; id?: number } | null {
  // /projects/123, /initiatives/45, /releases/67, /tickets, /ideas …
  const m = pathname.match(/^\/(projects|initiatives|releases)\/(\d+)/);
  if (m) {
    const map: Record<string, string> = {
      projects: 'project',
      initiatives: 'initiative',
      releases: 'release',
    };
    return { type: map[m[1]] ?? m[1], id: Number(m[2]) };
  }
  if (pathname.startsWith('/projects')) return { type: 'projects' };
  if (pathname.startsWith('/initiatives')) return { type: 'initiatives' };
  if (pathname.startsWith('/ideas')) return { type: 'ideas' };
  if (pathname.startsWith('/tickets')) return { type: 'tickets' };
  if (pathname.startsWith('/portfolio')) return { type: 'portfolio' };
  return null;
}
